import { useState, useEffect, ChangeEvent } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Header, { AppSection } from '@/components/Header';
import FirmenprofilSection from '@/components/FirmenprofilSection';
import { getApiUrl } from '@/lib/api';

interface SearchParams {
  keywords: string;
  startDate: string;
  endDate: string;
  country: string;
  projectSource: string;
}

interface Project {
  id: string;
  title: string;
  publication_date?: string;
  authority?: string;
  country?: string;
  description?: string;
  ted_link?: string;
  servicebund_link?: string;
  projectSource?: string;
}

interface Profile {
  id: string;
  name: string;
}

// Used for type safety when files need previews
// @ts-ignore - This is temporarily unused but will be used for file preview functionality
interface FileWithPreview extends File {
  preview: string;
}

// Add these helper components at the top of the file after the imports
const ListSection = ({ title, items }: { title: string; items: string[] }) => (
  <div className="mb-6">
    <h4 className="text-lg font-medium mb-2">{title}</h4>
    <ul className="list-disc list-inside space-y-1">
      {items.map((item, index) => (
        <li key={index} className="text-muted-foreground">{item}</li>
      ))}
    </ul>
  </div>
);

const ExperienceSection = ({ data }: { data: any }) => (
  <div className="space-y-6 bg-card p-6 rounded-lg border">
    <ListSection title="Projekttypen" items={data.types_of_projects || []} />
    <ListSection title="Technische Bereiche" items={data.technical_domains || []} />
    <ListSection title="Projektgrößen" items={data.project_sizes || []} />
    <ListSection title="Kernkompetenzen" items={data.key_strengths || []} />
    <ListSection title="Erfolgsmuster" items={data.success_patterns || []} />
  </div>
);

const TeamCapabilities = ({ data }: { data: any }) => (
  <div className="space-y-6 bg-card p-6 rounded-lg border">
    {Object.entries(data).map(([category, items]: [string, any]) => (
      <ListSection 
        key={category} 
        title={formatCategoryTitle(category)} 
        items={Array.isArray(items) ? items : []} 
      />
    ))}
  </div>
);

const formatCategoryTitle = (category: string) => {
  const titles: Record<string, string> = {
    technical_skills: "Technische Fähigkeiten",
    project_experience: "Projekterfahrung",
    certifications: "Zertifizierungen",
    specializations: "Spezialisierungen",
    languages: "Sprachen",
    soft_skills: "Soft Skills"
  };
  return titles[category] || category;
};

const ProjectAnalysis = ({ analysis }: { analysis: any }) => (
  <div className="space-y-4 bg-card p-6 rounded-lg border">
    {analysis.feasibility && (
      <div>
        <h4 className="text-lg font-medium mb-4">Qualifikationsanalyse</h4>
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-muted p-4 rounded-lg">
            <span>Übereinstimmung mit Anforderungen:</span>
            <span className="font-bold">{analysis.feasibility.feasibility_score}%</span>
          </div>
          {analysis.feasibility.matching_qualifications && (
            <div>
              <h5 className="font-medium mb-2">Vorhandene Qualifikationen</h5>
              <ul className="list-disc list-inside space-y-1">
                {analysis.feasibility.matching_qualifications.map((qual: string, index: number) => (
                  <li key={index} className="text-muted-foreground">{qual}</li>
                ))}
              </ul>
            </div>
          )}
          {analysis.feasibility.missing_qualifications && (
            <div>
              <h5 className="font-medium mb-2">Fehlende Qualifikationen</h5>
              <ul className="list-disc list-inside space-y-1">
                {analysis.feasibility.missing_qualifications.map((qual: string, index: number) => (
                  <li key={index} className="text-muted-foreground">{qual}</li>
                ))}
              </ul>
            </div>
          )}
          {analysis.feasibility.recommendations && (
            <div>
              <h5 className="font-medium mb-2">Empfehlungen basierend auf CV-Daten</h5>
              <ul className="list-disc list-inside space-y-1">
                {analysis.feasibility.recommendations.map((rec: string, index: number) => (
                  <li key={index} className="text-muted-foreground">{rec}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    )}
    
    {analysis.team_match && (
      <div>
        <h4 className="text-lg font-medium mb-4">Team-Kompatibilität</h4>
        <div className="space-y-4">
          {analysis.team_match.available_expertise && (
            <div>
              <h5 className="font-medium mb-2">Verfügbare Expertise laut CV</h5>
              <ul className="list-disc list-inside space-y-1">
                {analysis.team_match.available_expertise.map((expertise: any, index: number) => (
                  <li key={index} className="text-muted-foreground">
                    {expertise.skill}: {expertise.level} ({expertise.years_experience} Jahre Erfahrung)
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    )}

    {analysis.proposal && (
      <div>
        <h4 className="text-lg font-medium mb-4">Projektspezifische Qualifikationen</h4>
        <div className="prose prose-sm max-w-none">
          {typeof analysis.proposal === 'string' ? (
            <p className="text-muted-foreground whitespace-pre-wrap">{analysis.proposal}</p>
          ) : (
            <div className="space-y-4">
              {Object.entries(analysis.proposal).map(([section, content]: [string, any]) => (
                <div key={section}>
                  <h4 className="text-lg font-medium mb-2">{formatProposalSection(section)}</h4>
                  <div className="space-y-2">
                    {typeof content === 'object' && content.cv_based_qualifications && (
                      <div>
                        <h5 className="font-medium text-sm mb-1">Nachgewiesene Qualifikationen</h5>
                        <ul className="list-disc list-inside space-y-1">
                          {content.cv_based_qualifications.map((qual: string, index: number) => (
                            <li key={index} className="text-muted-foreground">{qual}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )}

    {analysis.proposal && analysis.proposal.cost_analysis && (
      <div>
        <h4 className="text-lg font-medium mb-4">Kostenanalyse</h4>
        <div className="space-y-4">
          {analysis.proposal.cost_analysis.cost_breakdown ? (
            <div className="space-y-2">
              <table className="w-full">
                <tbody>
                  {Object.entries(analysis.proposal.cost_analysis.cost_breakdown).map(([category, details]: [string, any]) => (
                    <tr key={category} className="border-b last:border-b-0">
                      <td className="py-2 text-sm">{formatCostCategory(category)}</td>
                      <td className="py-2 text-right text-sm">
                        {typeof details === 'object' ? (
                          <div>
                            <div className="font-medium">{formatCurrency(details.amount)}</div>
                            {details.calculation_basis && (
                              <div className="text-xs text-muted-foreground">
                                {details.calculation_basis}
                              </div>
                            )}
                          </div>
                        ) : (
                          formatCurrency(details)
                        )}
                      </td>
                    </tr>
                  ))}
                  {analysis.proposal.cost_analysis.total && (
                    <tr className="font-medium">
                      <td className="py-2">Gesamtkosten</td>
                      <td className="py-2 text-right">{formatCurrency(analysis.proposal.cost_analysis.total)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {analysis.proposal.cost_analysis.notes && (
                <div className="text-sm text-muted-foreground mt-2">
                  <strong>Hinweise:</strong>
                  <ul className="list-disc list-inside mt-1">
                    {analysis.proposal.cost_analysis.notes.map((note: string, index: number) => (
                      <li key={index}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Keine detaillierte Kostenaufstellung verfügbar.
            </p>
          )}
        </div>
      </div>
    )}

    {analysis.proposal && analysis.proposal.timeline && (
      <div>
        <h4 className="text-lg font-medium mb-4">Zeitplan</h4>
        <div className="space-y-4">
          {analysis.proposal.timeline.phases ? (
            <div className="space-y-2">
              {analysis.proposal.timeline.phases.map((phase: any, index: number) => (
                <div key={index} className="border-b last:border-b-0 pb-2">
                  <div className="font-medium">{phase.name}</div>
                  <div className="text-sm text-muted-foreground">
                    Dauer: {phase.duration}
                    {phase.team_size && ` • Team: ${phase.team_size} Mitarbeiter`}
                  </div>
                  {phase.activities && (
                    <ul className="list-disc list-inside text-sm text-muted-foreground mt-1">
                      {phase.activities.map((activity: string, actIndex: number) => (
                        <li key={actIndex}>{activity}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Kein detaillierter Zeitplan verfügbar.
            </p>
          )}
        </div>
      </div>
    )}
  </div>
);

const formatProposalSection = (section: string) => {
  const titles: Record<string, string> = {
    executive_summary: "Zusammenfassung",
    technical_solution: "Technische Lösung",
    cost_breakdown: "Kostenkalkulation",
    budget_table: "Kostenaufstellung",
    timeline: "Zeitplan",
    milestones: "Meilensteine",
    team_structure: "Teamstruktur",
    key_personnel: "Schlüsselpersonal",
    installation_costs: "Installationskosten",
    consultation_fees: "Beratungskosten",
    training_expenses: "Schulungskosten",
    certifications: "Zertifizierungen",
    contingency_fund: "Rücklagen",
    approach: "Ansatz",
    budget: "Budget",
    team: "Team",
    risks: "Risiken",
    conclusion: "Fazit",
    // Budget table items
    personal_and_training: "Personal und Schulung",
    equipment_and_materials: "Ausrüstung und Materialien",
    project_management: "Projektmanagement",
    contingency: "Rücklagen",
    // Team capabilities
    experience_levels: "Erfahrungsstufen",
    domain_expertise: "Fachgebiete",
    team_composition: "Teamzusammensetzung",
    // Domain expertise items
    "Industrielle Rohrinstallation": "Industrielle Rohrinstallation",
    "Chemische und Produktionsanlagen": "Chemische und Produktionsanlagen",
    // Experience levels
    senior_experten: "Senior Experten",
    mittleres_niveau: "Mittleres Niveau",
    junior: "Junior Level",
    // Team composition
    staerken: "Stärken",
    abdeckung: "Abdeckung",
    luecken: "Lücken"
  };
  return titles[section] || section;
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

const formatCostCategory = (category: string): string => {
  const categories: Record<string, string> = {
    personnel_costs: "Personalkosten",
    material_costs: "Materialkosten",
    equipment_costs: "Ausrüstungskosten",
    travel_costs: "Reisekosten",
    training_costs: "Schulungskosten",
    certification_costs: "Zertifizierungskosten",
    consultation_fees: "Beratungskosten",
    overhead_costs: "Gemeinkosten",
    contingency: "Risikorücklagen"
  };
  return categories[category] || category;
};

const downloadProposalAsPDF = async (projectTitle: string, proposal: any) => {
  try {
    toast.info('Generating PDF...', { autoClose: false });

    // Use the correct endpoint path without /api prefix
    const response = await fetch('/generate-pdf', { 
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title: projectTitle,
        proposal: proposal
      })
    });

    if (!response.ok) {
      let errorText = 'PDF generation failed';
      try {
        const errorData = await response.json();
        errorText = errorData.detail || errorText;
      } catch (_) {
        errorText = await response.text() || errorText;
      }
      toast.dismiss(); // Dismiss loading toast
      toast.error(`PDF generation failed: ${errorText}`);
      throw new Error(`PDF generation failed: ${errorText}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Projektvorschlag-${projectTitle}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    toast.dismiss(); // Dismiss loading toast
    toast.success('PDF generated successfully!');
  } catch (error) {
    console.error('Error generating PDF:', error);
    toast.dismiss(); // Dismiss loading toast in case of fetch error
    toast.error(`PDF generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

function App() {
  // State for active main section
  const [activeSection, setActiveSection] = useState<'search' | 'profile'>('search');

  // State for search parameters
  const [searchParams, setSearchParams] = useState<SearchParams>({
    keywords: '',
    startDate: '',
    endDate: '',
    country: 'AT',
    projectSource: 'ted'
  });

  // State for profiles
  const [profiles, setProfiles] = useState<Profile[]>([]); // To be fetched from API
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(true); // Add loading state for profiles

  // State for loading states
  const [isSearching, setIsSearching] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // State for search results and selected projects
  const [searchResults, setSearchResults] = useState<Project[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<Record<string, any>>({});

  // State for analysis results
  const [analysisResults, setAnalysisResults] = useState<any>(null);

  // --- Real API interactions for Profiles --- 
  useEffect(() => {
    const fetchProfiles = async () => {
      setIsLoadingProfiles(true);
      try {
        const apiUrl = getApiUrl('/api/profiles');
        console.log(`Fetching profiles from: ${apiUrl}`);
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        });

        console.log(`Profile fetch response status: ${response.status}`);
        console.log(`Response headers:`, Object.fromEntries([...response.headers.entries()]));
        
        // For debugging - get raw response text first
        const responseText = await response.text();
        console.log(`Raw profile response text: ${responseText}`);
        
        if (!response.ok) {
          console.error("Profile fetch error details:", responseText);
          throw new Error(`Failed to fetch profiles: ${response.status} ${response.statusText}`);
        }
        
        // Parse response text as JSON
        let fetchedProfiles: Profile[];
        try {
          fetchedProfiles = JSON.parse(responseText);
        } catch (jsonError) {
          console.error("Could not parse profiles response as JSON:", jsonError);
          throw new Error(`Invalid JSON response from server`);
        }
        
        setProfiles(fetchedProfiles);
        // Select the first profile if available and none is selected
        if (fetchedProfiles.length > 0 && !selectedProfileId) { 
          setSelectedProfileId(fetchedProfiles[0].id);
        }
      } catch (error) {
         toast.error(`Fehler beim Laden der Profile: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
         console.error("Error fetching profiles:", error);
      } finally {
         setIsLoadingProfiles(false);
      }
    };
    fetchProfiles();
    // Run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, []); 

  const handleCreateProfile = async (name: string): Promise<Profile | null> => {
     try {
        console.log(`Sending create profile request for "${name}"`);
        
        const apiUrl = getApiUrl('/api/profiles');
        console.log(`Making API call to: ${apiUrl}`);
        
        const response = await fetch(apiUrl, {
           method: 'POST',
           headers: {
              'Content-Type': 'application/json',
           },
           body: JSON.stringify({ name: name }),
        });

        console.log(`Profile creation response status: ${response.status}`);
        console.log(`Response headers:`, Object.fromEntries([...response.headers.entries()]));
        
        // For debugging - try to get the raw response text
        const responseText = await response.text();
        console.log(`Raw response text: ${responseText}`);
        
        if (!response.ok) {
           // Don't try to parse JSON if we already have the text
           console.error("Profile creation error details:", responseText);
           throw new Error(`Failed to create profile: ${response.status} ${response.statusText}`);
        }

        // Parse the response text as JSON
        let newProfile: Profile;
        try {
           newProfile = JSON.parse(responseText);
        } catch (jsonError) {
           console.error("Could not parse response as JSON:", jsonError);
           throw new Error(`Invalid JSON response from server`);
        }
        
        console.log("Created profile:", newProfile);
        setProfiles(prev => [...prev, newProfile]);
        setSelectedProfileId(newProfile.id); // Select the new profile
        toast.success(`Profil "${name}" erfolgreich erstellt.`);
        return newProfile;
     } catch (error) {
        toast.error(`Fehler beim Erstellen des Profils: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
        console.error("Error creating profile:", error);
        return null; // Indicate failure
     }
  };
  // --- End Real API interactions ---

  // Add useEffect hooks for debugging
  useEffect(() => {
    if (analysisResults) {
      console.log('Analysis results updated:', analysisResults);
    }
  }, [analysisResults]);

  // Handle search parameter changes
  const handleSearchParamChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setSearchParams(prev => {
       const newState = { ...prev, [name]: value };
       // Logic to switch projectSource if country changes from DE
       if (name === 'country' && value !== 'DE' && newState.projectSource === 'servicebund') {
         newState.projectSource = 'ted';
       }
       return newState;
    });
  };

  // Handle project selection
  const handleProjectSelect = (projectId: string, project: any) => {
    setSelectedProjects(prev => {
      if (prev[projectId]) {
        const { [projectId]: _, ...rest } = prev;
        return rest;
      } else {
        return { ...prev, [projectId]: project };
      }
    });
  };

  // Handle Search (Modify formatting to include projectSource)
  const handleSearch = async () => {
    setIsSearching(true);
    setSearchResults([]); 
    setAnalysisResults(null); 
    const currentSource = searchParams.projectSource; 
    try {
      console.log('Sending search request with params:', searchParams);
      
      const apiUrl = getApiUrl('/api/search');
      console.log(`Making search API call to: ${apiUrl}`);
      
      const response = await fetch(apiUrl, { 
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(searchParams) 
      });

      console.log('Response status:', response.status);

      // Improved error handling: Check status first, then parse body once
      if (!response.ok) {
        let errorText = `HTTP error ${response.status}`;
        try {
            // Try parsing JSON error detail from backend first
            const errorData = await response.json(); 
            errorText = errorData.detail || errorText;
        } catch (jsonError) {
            // If JSON parsing fails, try getting plain text
            try {
               errorText = await response.text() || errorText;
            } catch (textError) {
               // Keep the original status text if text parsing also fails 
               console.error("Failed to parse error response body", textError);
            } 
        }
        // Throw the consolidated error message
        throw new Error(errorText); 
      }

      const data = await response.json(); // This should succeed if response.ok is true
      console.log('Raw response data:', data);

      if (!data.results || !Array.isArray(data.results)) {
        console.error('Invalid response format:', data);
        toast.error("Ungültiges Format der Suchergebnisse erhalten.");
        setSearchResults([]); 
        return;
      }

      // Format results AND add the projectSource used for the search
      const formattedResults = data.results.map((result: any): Project => ({ 
        id: result.id || String(Math.random()),
        title: typeof result.title === 'object' ? 
          (result.title?.deu || 'Untitled Project') : 
          (result.title || 'Untitled Project'),
        publication_date: result.publication_date || 'Not specified',
        authority: typeof result.authority === 'object' ? 
          (result.authority?.deu ? (Array.isArray(result.authority.deu) ? result.authority.deu[0] : result.authority.deu) : 'Not specified') : 
          (result.authority || 'Not specified'),
        country: result.country || 'Not specified',
        description: result.description || 'No description available',
        ted_link: result.ted_link || '#',
        servicebund_link: result.servicebund_link || '#',
        projectSource: currentSource 
      }));

      console.log('Formatted results:', formattedResults);
      setSearchResults(formattedResults);
      if (formattedResults.length === 0) {
          toast.info("Keine Projekte für Ihre Suche gefunden.");
      }

    } catch (error) {
      console.error('Search failed:', error);
       // Display the error message thrown from the try block
      toast.error(`Fehler bei der Suche: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`); 
      setSearchResults([]); 
    } finally {
      setIsSearching(false);
    }
  };

  // Handle analysis - MODIFIED for real API call
  const handleAnalyze = async () => {
    if (!selectedProfileId) {
       toast.error("Bitte wählen Sie zuerst ein Firmenprofil aus.");
       return;
    }
    if (Object.keys(selectedProjects).length === 0) {
       toast.error("Bitte wählen Sie mindestens ein Projekt für die Analyse aus.");
       return;
    }

    const toastId = toast.loading('Analyse läuft ...', { closeButton: false });

    try {
      setIsAnalyzing(true);
      setAnalysisResults(null);

      const formData = new FormData();
      formData.append('selectedProfileId', selectedProfileId);
      formData.append('selectedProjects', JSON.stringify(selectedProjects));

      const apiUrl = getApiUrl('/api/analyze');
      console.log(`Making analyze API call to: ${apiUrl}`);
      
      const response = await fetch(apiUrl, { 
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        let errorText = 'Analyse fehlgeschlagen';
        try {
            const errorData = await response.json();
            errorText = errorData.detail || errorText;
        } catch (_) {
            errorText = await response.text() || errorText; // Fallback to text
        }
        toast.update(toastId, {
           render: `Analyse fehlgeschlagen: ${errorText}`,
           type: 'error',
           isLoading: false,
           autoClose: 5000,
           closeButton: true, 
        });
        throw new Error(`Analysis failed: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      setAnalysisResults(data);
      toast.update(toastId, {
         render: 'Analyse erfolgreich abgeschlossen!',
         type: 'success',
         isLoading: false,
         autoClose: 3000,
         closeButton: true,
      });

    } catch (error) {
      console.error('Analysis error:', error);
      // Check if the loading toast is still active
      if (toast.isActive(toastId)) {
         // If active, update it to show the error
         toast.update(toastId, {
            render: `Analysefehler: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`,
            type: 'error',
            isLoading: false,
            autoClose: 5000,
            closeButton: true,
         });
      } else {
         // Otherwise (e.g., error before fetch or toast timed out), create a new error toast
         toast.error(`Analysefehler: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Define handler to change active section
  const handleSectionChange = (section: AppSection) => {
    setActiveSection(section);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4">
        <Header onSectionChange={handleSectionChange} />
        <main className="py-6">
          {/* Conditional Rendering based on activeSection */}
          {activeSection === 'search' && (
            <div className="space-y-6">
              {/* Project Search Section */}
              <div className="border rounded-lg p-4">
                 <div className="mb-4">
                   <h3 className="text-xl font-semibold">Projektsuche</h3>
                   <p className="text-sm text-muted-foreground">Suchen Sie nach Projekten und wählen Sie Profile für die Analyse aus.</p>
                 </div>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Search Inputs Column */}
                    <div className="space-y-4">
                       <input
                         type="text"
                         name="keywords"
                         value={searchParams.keywords}
                         onChange={handleSearchParamChange}
                         placeholder="z.B. Wasserstoff, Energiespeicher..."
                         className="w-full px-3 py-2 border rounded-md"
                       />
                       <div className="grid grid-cols-2 gap-4">
                         <div>
                           <label className="block text-sm mb-1">Von</label>
                           <input
                             type="date"
                             name="startDate"
                             value={searchParams.startDate}
                             onChange={handleSearchParamChange}
                             className="w-full px-3 py-2 border rounded-md"
                           />
                         </div>
                         <div>
                           <label className="block text-sm mb-1">Bis</label>
                           <input
                             type="date"
                             name="endDate"
                             value={searchParams.endDate}
                             onChange={handleSearchParamChange}
                             className="w-full px-3 py-2 border rounded-md"
                           />
                         </div>
                       </div>
                       <div>
                         <label className="block text-sm mb-1">Land</label>
                         <select
                           name="country"
                           value={searchParams.country}
                           onChange={handleSearchParamChange} // Combined state update logic here now
                           className="w-full px-3 py-2 border rounded-md"
                         >
                           <option value="AT">Österreich</option>
                           <option value="DE">Deutschland</option>
                           <option value="CH">Schweiz</option>
                           <option value="FR">Frankreich</option>
                           <option value="IT">Italien</option>
                           <option value="NL">Niederlande</option>
                           <option value="BE">Belgien</option>
                         </select>
                       </div>
                       <div>
                         <label className="block text-sm mb-1">Projektquelle</label>
                         <select
                           name="projectSource"
                           value={searchParams.projectSource}
                           onChange={handleSearchParamChange}
                           className="w-full px-3 py-2 border rounded-md"
                         >
                           <option value="ted">TED</option>
                           <option value="servicebund" disabled={searchParams.country !== 'DE'}>bund.de</option>
                         </select>
                       </div>
                       <button
                         onClick={handleSearch}
                         disabled={isSearching}
                         className="w-full bg-primary text-primary-foreground px-4 py-2 rounded-md disabled:opacity-50"
                       >
                         {isSearching ? 'Suche läuft...' : 'Projekte Suchen'}
                       </button>
                     </div>

                     {/* Analysis Trigger Column (Reverted to HTML select) */}
                     <div className="space-y-4 border-l pl-4">
                        <h4 className="text-lg font-semibold">Analyse Konfiguration</h4>
                        {isLoadingProfiles ? (
                            <p className="text-sm text-muted-foreground">Lade Profile...</p>
                        ) : profiles.length > 0 ? (
                           <div>
                              <label htmlFor="profile-select" className="block text-sm mb-1">Firmenprofil für Analyse</label>
                              {/* Reverted to standard HTML select */}
                              <select
                                 id="profile-select"
                                 value={selectedProfileId ?? ''}
                                 onChange={(e: ChangeEvent<HTMLSelectElement>) => setSelectedProfileId(e.target.value || null)}
                                 className="w-full px-3 py-2 border rounded-md mb-4 bg-background text-foreground"
                              >
                                 <option value="" disabled>Profil auswählen...</option>
                                 {profiles.map(profile => (
                                    <option key={profile.id} value={profile.id}>
                                       {profile.name}
                                    </option>
                                 ))}
                              </select>

                              <Button
                                 onClick={handleAnalyze}
                                 disabled={isAnalyzing || !selectedProfileId || Object.keys(selectedProjects).length === 0}
                                 className="w-full bg-accent text-accent-foreground px-4 py-2 rounded-md disabled:opacity-50 mt-4"
                                 title={!selectedProfileId ? "Bitte ein Profil auswählen" : Object.keys(selectedProjects).length === 0 ? "Bitte Projekte in den Suchergebnissen auswählen" : "Analyse starten"}
                               >
                                 {isAnalyzing ? 'Analyse läuft...' : 'Analyse Starten'}
                               </Button>
                               <p className="text-xs text-muted-foreground mt-2">
                                 Hinweis: Wählen Sie Projekte aus der Ergebnisliste unten aus, um die Analyse zu starten.
                               </p>
                           </div>
                        ) : (
                           <p className="text-sm text-muted-foreground">
                              Keine Firmenprofile gefunden. Bitte erstellen Sie zuerst ein Profil im Tab "Firmenprofile verwalten".
                           </p>
                        )}
                     </div>
                 </div>
              </div>

              {/* Results Area (Tabs) - Stays in the 'search' section */}
              <div className="mt-8">
                 <Tabs defaultValue="search" className="w-full">
                   <TabsList className="grid w-full grid-cols-3">
                     <TabsTrigger value="search">Suchergebnisse</TabsTrigger>
                     <TabsTrigger value="analysis">Analyseergebnisse</TabsTrigger>
                     <TabsTrigger value="proposal">Projektvorschlag</TabsTrigger>
                   </TabsList>
                   <TabsContent value="search">
                     <div className="rounded-md border p-4 min-h-[200px]"> {/* Added min-height */}
                       {isSearching ? (
                           <p className="text-muted-foreground">Suche läuft...</p>
                       ) : searchResults && searchResults.length > 0 ? (
                         <div className="space-y-6">
                           {searchResults.map((project: Project) => ( // Use Project interface
                             <div key={project.id || Math.random()} className="border-b pb-4 last:border-b-0"> {/* Removed last border */}
                               <div className="flex items-start justify-between">
                                 <div className="flex-1 mr-4"> {/* Added flex-1 and margin */}
                                   <h3 className="text-lg font-semibold">
                                     {project.title || 'Unbenanntes Projekt'}
                                   </h3>
                                   <p className="text-sm text-muted-foreground">
                                     Veröffentlicht am: {project.publication_date?.replace('Z', '') || 'Nicht angegeben'}
                                   </p>
                                   <p className="text-sm text-muted-foreground">
                                     Auftraggeber: {project.authority || 'Nicht angegeben'}
                                   </p>
                                   <p className="text-sm text-muted-foreground">
                                     Land: {project.country === 'Not specified' ? 'Nicht angegeben' : (project.country || 'Nicht angegeben')}
                                   </p>
                                   <div className="flex space-x-4 mt-1"> {/* Links side-by-side */}
                                      {project.ted_link && project.ted_link !== '#' && (
                                         <a href={project.ted_link} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                                            {/* Clarify link source */} 
                                            {project.projectSource === 'servicebund' ? 'Portal öffnen (Extern)' : 'Portal öffnen (TED)'} 
                                         </a>
                                      )}
                                      {project.servicebund_link && project.servicebund_link !== '#' && (
                                         <a href={project.servicebund_link} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                                            {/* Clarify link source */} 
                                            Portal öffnen (bund.de)
                                         </a>
                                      )}
                                   </div>
                                 </div>
                                 <Button // Changed button style
                                   variant={selectedProjects[project.id] ? "default" : "secondary"}
                                   size="sm"
                                   onClick={() => handleProjectSelect(project.id, project)}
                                 >
                                   {selectedProjects[project.id] ? 'Ausgewählt' : 'Auswählen'}
                                 </Button>
                               </div>
                               {project.description && (
                                 <p className="mt-2 text-sm text-muted-foreground">
                                   {project.description === 'No description available' ? 'Keine Beschreibung verfügbar' : (project.description || 'Keine Beschreibung verfügbar')}
                                 </p>
                               )}
                             </div>
                           ))}
                         </div>
                       ) : (
                         <p className="text-muted-foreground">
                           Führen Sie eine Suche durch, um hier Ergebnisse zu sehen, oder passen Sie Ihre Suchfilter an.
                         </p>
                       )}
                     </div>
                   </TabsContent>
                    <TabsContent value="analysis">
                     <div className="rounded-md border p-4 space-y-6 min-h-[200px]"> {/* Added min-height */}
                       {isAnalyzing ? (
                           <p className="text-muted-foreground">Analyse wird verarbeitet...</p>
                       ) : analysisResults ? (
                         <>
                           {analysisResults.company_experience && (
                             <div className="space-y-4">
                               <h3 className="text-xl font-semibold">Unternehmenserfahrung</h3>
                               <ExperienceSection data={analysisResults.company_experience} />
                             </div>
                           )}

                           {analysisResults.team_capabilities && (
                             <div className="space-y-4">
                               <h3 className="text-xl font-semibold">Team-Fähigkeiten</h3>
                               <TeamCapabilities data={analysisResults.team_capabilities} />
                             </div>
                           )}

                           {analysisResults.project_analyses && Object.entries(analysisResults.project_analyses).length > 0 && (
                             <div className="space-y-4">
                               <h3 className="text-xl font-semibold">Projektanalysen</h3>
                               {Object.entries(analysisResults.project_analyses).map(([projectId, analysis]: [string, any]) => (
                                 <div key={projectId} className="space-y-4 border-t pt-4 first:border-t-0 first:pt-0">
                                   <h4 className="text-lg font-medium">{analysis.project?.title || `Projekt ${projectId}`}</h4>
                                   <ProjectAnalysis analysis={analysis} />
                                   <div className="flex items-center space-x-4"> {/* Links side-by-side */}
                                     {analysis.project.ted_link && analysis.project.ted_link !== '#' && (
                                       <a href={analysis.project.ted_link} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                                         {/* Clarify link source */} 
                                         {analysis.project.projectSource === 'servicebund' ? 'Portal öffnen (Extern)' : 'Portal öffnen (TED)'} 
                                       </a>
                                     )}
                                     {analysis.project.servicebund_link && analysis.project.servicebund_link !== '#' && (
                                       <a href={analysis.project.servicebund_link} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                                         {/* Clarify link source */} 
                                         Portal öffnen (bund.de)
                                       </a>
                                     )}
                                   </div>
                                 </div>
                               ))}
                             </div>
                           )}

                           {(!analysisResults.company_experience &&
                             !analysisResults.team_capabilities &&
                             (!analysisResults.project_analyses || Object.entries(analysisResults.project_analyses).length === 0)) && (
                             <p className="text-muted-foreground">
                               Die Analyse wurde durchgeführt, aber es wurden keine auswertbaren Ergebnisse für die ausgewählten Projekte und das Profil gefunden.
                             </p>
                           )}
                         </>
                       ) : (
                         <p className="text-muted-foreground">
                           Führen Sie eine Analyse durch (Projekte und Profil auswählen), um hier Ergebnisse zu sehen.
                         </p>
                       )}
                     </div>
                   </TabsContent>
                   <TabsContent value="proposal">
                      <div className="rounded-md border p-4 space-y-6 min-h-[200px]"> {/* Added min-height */}
                         {analysisResults && analysisResults.project_analyses && Object.values(analysisResults.project_analyses).some((a: any) => a.proposal) ? (
                            Object.entries(analysisResults.project_analyses)
                            .filter(([_, analysis]: [string, any]) => analysis.proposal)
                            .map(([projectId, analysis]: [string, any]) => (
                               <div key={projectId} className="space-y-4 border-t pt-4 first:border-t-0 first:pt-0">
                               <div className="flex items-center justify-between">
                                  <h3 className="text-xl font-semibold">
                                     {analysis.project?.title || `Projekt ${projectId}`}
                                  </h3>
                                  <Button // Use Button component
                                     onClick={() => downloadProposalAsPDF(
                                     analysis.project?.title || `Projekt ${projectId}`,
                                     analysis.proposal
                                     )}
                                     size="sm"
                                  >
                                     Als PDF herunterladen
                                  </Button>
                               </div>
                               <div className="bg-card p-6 rounded-lg border">
                                  <div className="prose prose-sm max-w-none">
                                     {typeof analysis.proposal === 'string' ? (
                                     <p className="text-muted-foreground whitespace-pre-wrap">{analysis.proposal}</p>
                                     ) : (
                                     <div className="space-y-4">
                                        {Object.entries(analysis.proposal).map(([section, content]: [string, any]) => (
                                           <div key={section}>
                                           <h4 className="text-lg font-medium mb-2">{formatProposalSection(section)}</h4>
                                           {typeof content === 'string' ? (
                                              <p className="text-muted-foreground whitespace-pre-wrap">{content}</p>
                                           ) : content && typeof content === 'object' ? (
                                              <div className="space-y-2">
                                              {Object.entries(content).map(([subSection, subContent]: [string, any]) => (
                                                 <div key={subSection}>
                                                    <h5 className="font-medium text-sm mb-1">{subSection}</h5>
                                                    <p className="text-muted-foreground whitespace-pre-wrap">
                                                    {typeof subContent === 'string' ? subContent : JSON.stringify(subContent)}
                                                    </p>
                                                 </div>
                                              ))}
                                              </div>
                                           ) : (
                                              <p className="text-muted-foreground">Keine Details verfügbar</p>
                                           )}
                                           </div>
                                        ))}
                                     </div>
                                     )}
                                  </div>
                               </div>
                               </div>
                            ))
                         ) : (
                            <p className="text-muted-foreground">
                              Führen Sie eine Analyse durch. Projektvorschläge werden hier angezeigt, falls generiert.
                            </p>
                         )}
                      </div>
                   </TabsContent>
                 </Tabs>
              </div>
            </div>
          )}

          {activeSection === 'profile' && (
             <FirmenprofilSection
               profiles={profiles}
               selectedProfileId={selectedProfileId}
               onSelectProfile={setSelectedProfileId}
               onCreateProfile={handleCreateProfile}
             />
          )}

        </main>
      </div>
      <ToastContainer position="bottom-right" />
    </div>
  );
}

export default App;
